import { Component, ElementRef, ViewChild, ViewEncapsulation, type OnDestroy, type OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators, type FormControlStatus, type UntypedFormGroup } from '@angular/forms';

import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';

import { LoaderType } from '@fcom/common/';
import { GtmService } from '@fcom/common/gtm';
import { ScrollService } from '@fcom/common/services/scroll/scroll.service';
import { type Profile } from '@fcom/core-api/login';
import { type AppState } from '@fcom/core/interfaces';
import { profileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors';
import { EMAIL_PATTERN, LocalDate, NAME_PATTERN, unsubscribe, WORLDTRACER_PATTERN } from '@fcom/core/utils';
import {
  dateValidator,
  disableConditionalFields,
  enableConditionalFields,
  FormInputType,
  FormStatus,
  pnrFormatValidator,
  ReactiveFormStatus,
  trackFormState,
} from '@fcom/service-forms';
import { DatepickerTheme } from '@fcom/service-forms/components/common/datepicker.component';
import { ButtonType, NotificationLayout, NotificationTheme, RadioItemGroupOption } from '@fcom/ui-components';

import { CertificatesForInsuranceCompanyField, CertificatesForInsuranceCompanyReason } from '../interfaces';
import { SubmitService } from '../services/submit.service';
import { FLIGHT_NUMBER_PATTERN, parsePhoneNumber, PHONE_NUMBER_PATTERN } from '../utils';

const CERTIFICATES_FOR_INSURANCE_COMPANY_FORM = 'certificates-for-insurance-company-form';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'fin-certificates-for-insurance-company-form',
  styleUrls: ['./certificates-for-insurance-company-form.component.scss'],
  templateUrl: 'certificates-for-insurance-company-form.component.html',
})
export class CertificatesForInsuranceCompanyFormComponent implements OnInit, OnDestroy {
  readonly ButtonType = ButtonType;
  readonly CertificatesForInsuranceCompanyField = CertificatesForInsuranceCompanyField;
  readonly DatepickerTheme = DatepickerTheme;
  readonly FormInputType = FormInputType;
  readonly FormStatus = FormStatus;
  readonly LoaderType = LoaderType;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationTheme = NotificationTheme;

  @ViewChild('formContainer', { static: true }) formContainer: ElementRef;

  /**
   * Conditional form controls that enable one or several other fields
   * depending on what option has been selected.
   *
   * The first key refers to the control triggering the changes,
   * while the first value of each of its descendant arrays determine
   * the value of the option selected and the fields it should enable.
   */
  formConditionalControls: Record<string, [string, string[]][]> = {
    [CertificatesForInsuranceCompanyField.REASON_FOR_CERTIFICATE]: [
      [
        CertificatesForInsuranceCompanyReason.BAGGAGE_DELAYED_OR_DAMAGED,
        [CertificatesForInsuranceCompanyField.FLIGHTS, CertificatesForInsuranceCompanyField.WORLD_TRACER_NUMBER],
      ],
      [
        CertificatesForInsuranceCompanyReason.SELF_CANCELLED_BOOKING,
        [CertificatesForInsuranceCompanyField.CONFIRM_SELF_CANCELLED_BOOKING],
      ],
      [
        CertificatesForInsuranceCompanyReason.FLIGHT_DELAYED_OR_CANCELLED,
        [CertificatesForInsuranceCompanyField.FLIGHTS],
      ],
    ],
  };
  /**
   * Group configurations for form arrays that contain the name
   * of the group as its key and a record of repeatable fields
   * with their default values and validation patterns.
   */
  formGroupConfig: Record<string, Record<string, unknown>> = {
    [CertificatesForInsuranceCompanyField.FLIGHTS]: {
      [CertificatesForInsuranceCompanyField.FLIGHT_DATE]: ['', [Validators.required, dateValidator()]],
      [CertificatesForInsuranceCompanyField.FLIGHT_NUMBER]: [
        '',
        [Validators.required, Validators.pattern(FLIGHT_NUMBER_PATTERN)],
      ],
    },
  };
  /**
   * Groups of checkbox and radio input groups containing the
   * name of the group as its key and an array of options that
   * will be looped over to create the inputs to set its value.
   *
   * Translation keys for the options should be stored in the
   * same place as the translations for the group fields.
   */
  formInputGroupOptions: Record<string, RadioItemGroupOption[]> = {
    [CertificatesForInsuranceCompanyField.REASON_FOR_CERTIFICATE]: [
      { value: CertificatesForInsuranceCompanyReason.BAGGAGE_DELAYED_OR_DAMAGED },
      { value: CertificatesForInsuranceCompanyReason.FLIGHT_DELAYED_OR_CANCELLED },
      { value: CertificatesForInsuranceCompanyReason.NO_SHOW },
      { value: CertificatesForInsuranceCompanyReason.ONBOARD },
      { value: CertificatesForInsuranceCompanyReason.SELF_CANCELLED_BOOKING },
    ],
  };
  formStatus: FormStatus = FormStatus.INITIALIZING;
  minDate: LocalDate;
  maxDate: LocalDate;
  reactiveForm: UntypedFormGroup;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private fb: UntypedFormBuilder,
    private gtmService: GtmService,
    private scrollService: ScrollService,
    private store$: Store<AppState>,
    private submitService: SubmitService
  ) {}

  get allConditionalFields(): CertificatesForInsuranceCompanyField[] {
    return Array.from(
      new Set(
        Object.keys(this.formConditionalControls).flatMap(
          (control: CertificatesForInsuranceCompanyField): CertificatesForInsuranceCompanyField[] =>
            this.formConditionalControls[control].flatMap(
              ([, fields]: [
                CertificatesForInsuranceCompanyField,
                CertificatesForInsuranceCompanyField[],
              ]): CertificatesForInsuranceCompanyField[] => fields
            )
        )
      )
    );
  }

  get datepickerThemes(): typeof DatepickerTheme {
    return DatepickerTheme;
  }

  ngOnInit(): void {
    this.minDate = LocalDate.now().minusMonths(6);
    this.maxDate = LocalDate.now().plusMonths(24);

    this.subscriptions.add(
      this.store$
        .pipe(profileOrUndefinedIfNotLoggedIn(), distinctUntilChanged())
        .subscribe((profile: Profile): void => {
          this.createForm(profile);
          this.formStatus = FormStatus.INITIAL;
          disableConditionalFields(this.allConditionalFields, this.reactiveForm);
          this.addFormWatchers();
        })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  onSubmit(): void {
    if (!this.reactiveForm.valid) {
      this.reactiveForm.markAllAsTouched();
      this.reactiveForm.updateValueAndValidity();

      return;
    }

    this.formStatus = FormStatus.LOADING;
    this.scrollService.scrollToImmediate(this.formContainer);
    trackFormState(CERTIFICATES_FOR_INSURANCE_COMPANY_FORM, 'send-attempt', this.gtmService);
    this.subscriptions.add(
      this.submitService
        .certificatesForInsuranceCompany(this.reactiveForm.value)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.formStatus = FormStatus.COMPLETE;
            trackFormState(CERTIFICATES_FOR_INSURANCE_COMPANY_FORM, 'success', this.gtmService);
          },
          error: () => {
            this.formStatus = FormStatus.ERROR;
            trackFormState(CERTIFICATES_FOR_INSURANCE_COMPANY_FORM, 'error', this.gtmService);
          },
        })
    );
  }

  private addFormWatchers(): void {
    this.subscriptions.add(
      this.reactiveForm.statusChanges
        .pipe(
          filter((status: FormControlStatus): boolean => status === ReactiveFormStatus.INVALID),
          distinctUntilChanged(),
          take(1)
        )
        .subscribe((): void => {
          trackFormState(CERTIFICATES_FOR_INSURANCE_COMPANY_FORM, 'start', this.gtmService);
        })
    );
    for (const formControl of Object.keys(this.formConditionalControls)) {
      this.subscriptions.add(
        this.reactiveForm.controls[formControl].valueChanges.subscribe(
          (value: CertificatesForInsuranceCompanyReason): void => this.toggleConditionalFields(formControl, value)
        )
      );
    }
  }

  private createForm(profile?: Profile): void {
    this.reactiveForm = this.fb.group({
      [CertificatesForInsuranceCompanyField.REASON_FOR_CERTIFICATE]: ['', Validators.required],
      [CertificatesForInsuranceCompanyField.FIRST_NAME]: [
        profile?.firstname ?? '',
        [Validators.required, Validators.pattern(NAME_PATTERN)],
      ],
      [CertificatesForInsuranceCompanyField.LAST_NAME]: [
        profile?.lastname ?? '',
        [Validators.required, Validators.pattern(NAME_PATTERN)],
      ],
      [CertificatesForInsuranceCompanyField.EMAIL]: [
        profile?.email ?? '',
        [Validators.required, Validators.pattern(EMAIL_PATTERN)],
      ],
      [CertificatesForInsuranceCompanyField.PHONE]: [
        parsePhoneNumber(profile),
        [Validators.required, Validators.pattern(PHONE_NUMBER_PATTERN)],
      ],
      [CertificatesForInsuranceCompanyField.BOOKING_REFERENCE]: ['', [Validators.required, pnrFormatValidator()]],
      [CertificatesForInsuranceCompanyField.TICKET_NUMBER]: ['', [Validators.required]],
      [CertificatesForInsuranceCompanyField.CONFIRM_SELF_CANCELLED_BOOKING]: [null, Validators.required],
      [CertificatesForInsuranceCompanyField.FLIGHTS]: this.fb.array([]),
      [CertificatesForInsuranceCompanyField.WORLD_TRACER_NUMBER]: [
        '',
        [Validators.required, Validators.pattern(WORLDTRACER_PATTERN)],
      ],
    });
  }

  private toggleConditionalFields(formControl: string, selectedOption: string): void {
    disableConditionalFields(this.allConditionalFields, this.reactiveForm);
    for (const [option, fields] of this.formConditionalControls[formControl]) {
      if (option !== selectedOption) {
        continue;
      }
      enableConditionalFields(fields, this.reactiveForm);
    }
    this.reactiveForm.updateValueAndValidity();
  }
}
